import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useClassNames, Button } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { PlaybackInformationContext } from '../../../../contexts/PlaybackInformationContext';

function stopPropagation(event) {
	event.stopPropagation();
}

type SubtitleTrackProps = {
	track: any;
	active: boolean;
	onSubtitleClicked: (event: any, track: any) => void;
};

const SubtitleTrack: React.FC<SubtitleTrackProps> = ({ track, active, onSubtitleClicked }) => {
	const { t } = useTranslation();
	const onClick = useCallback((event) => onSubtitleClicked(event, track), [onSubtitleClicked, track]);

	const className = useClassNames('', { active });

	return (
		<li className={className} onClick={onClick} onTouchStart={stopPropagation} onTouchEnd={stopPropagation}>
			{t(`Player.Languages.${track.language}`, track.language)}
		</li>
	);
};

export const PlaybackSettingsSubtitle: React.FC = () => {
	const { t } = useTranslation();
	const { player, playbackInformationVisible } = useContext(PlaybackInformationContext);

	const [open, setOpen] = useState(false);
	const [subtitleTracks, setSubtitleTracks] = useState([]);
	const activeSubtitleTrack = useMemo(() => {
		return subtitleTracks[subtitleTracks.findIndex((track) => track.mode === 'showing')] || null;
	}, [subtitleTracks]);

	useEffect(() => {
		const setTracks = () => {
			if (player) {
				setSubtitleTracks(player.getSubtitleTracks());
			}
		};

		player.addTextTracksListener(setTracks);

		setTracks();

		return () => {
			if (player) {
				player.removeTextTracksListener(setTracks);
			}
		};
	}, [player]);

	useEffect(() => {
		if (open && !playbackInformationVisible) {
			setOpen(false);
		}
	}, [open, playbackInformationVisible]);

	const onSubtitleButtonClick = useCallback(
		(event) => {
			event.preventDefault();
			event.stopPropagation();
			setOpen(!open);
		},
		[open]
	);

	const onSubtitleClicked = useCallback(
		(event, subtitleTrack) => {
			event.preventDefault();
			event.stopPropagation();

			if (activeSubtitleTrack) {
				activeSubtitleTrack.mode = 'disabled';
			}
			if (activeSubtitleTrack !== subtitleTrack) {
				// eslint-disable-next-line no-param-reassign
				subtitleTrack.mode = 'showing';
			}
		},
		[activeSubtitleTrack]
	);

	const subtitleComponents = useMemo(() => {
		return subtitleTracks.map((track) => <SubtitleTrack key={track.id} track={track} active={activeSubtitleTrack === track} onSubtitleClicked={onSubtitleClicked} />);
	}, [activeSubtitleTrack, onSubtitleClicked, subtitleTracks]);

	const className = useClassNames('playback-settings-subtitles', { selected: open });

	if (!player || subtitleTracks.length === 0) return null;

	return (
		<div className={className}>
			<Button onClick={onSubtitleButtonClick}>{t('Player.SubtitleLanguage')}</Button>
			<div className={'playback-settings-subtitles-selection'}>
				<ul>{subtitleComponents}</ul>
			</div>
		</div>
	);
};
